/**
 * DASHBOARD KPI DEFINITIONS — Registre enrichi pour le dashboard pilotage
 *
 * Chaque entrée contient toutes les métadonnées pédagogiques nécessaires
 * pour expliquer un KPI à un dirigeant : définition, formule, inclus/exclus,
 * période, hypothèses éventuelles.
 *
 * ⚠️ Ce fichier ne contient AUCUNE logique de calcul.
 *    Les calculs restent dans le backend Python.
 *    Ce fichier gère uniquement le wording et la documentation des KPI.
 */

export type KpiPeriod = "monthly" | "annual" | "instant";
export type KpiDomainType = "finance" | "staffing" | "operations";
export type KpiUnitType = "currency" | "percent" | "number" | "fte";

export type DashboardKpiDefinition = {
  key: string;
  /** Label court affiché dans l'UI (1–5 mots) */
  label: string;
  /** Description métier en français, compréhensible sans technique */
  description: string;
  /** Formule en langage naturel (pas de code) */
  formulaText: string;
  /** Ce qui est pris en compte dans ce KPI */
  included: string[];
  /** Ce qui est explicitement exclu */
  excluded?: string[];
  /** Fenêtre temporelle du KPI */
  period: KpiPeriod;
  /** Chemin backend — métadonnée technique, non affichée dans l'UI principale */
  source: string;
  /** Domaine fonctionnel */
  domain: KpiDomainType;
  /** Hypothèses ou mises en garde importantes */
  assumptions?: string[];
  /** Unité de mesure */
  unit: KpiUnitType;
  /** Vrai si la valeur est une estimation ou projection */
  isEstimate?: boolean;
};

export const DASHBOARD_KPI_REGISTRY: Record<string, DashboardKpiDefinition> = {

  // ─── VUE ANNUELLE ────────────────────────────────────────────────────────────

  ca_annuel_net: {
    key: "ca_annuel_net",
    label: "CA annuel net",
    description:
      "Chiffre d'affaires net généré par toutes les missions actives sur l'année, après déduction des commissions versées aux intermédiaires éventuels.",
    formulaText:
      "Σ (TJM client − commission intermédiaire) × jours facturés, pour chaque mission active sur l'année",
    included: [
      "Missions FREELANCE actives",
      "Missions CDI actives",
      "Tous les mois de l'année en cours",
    ],
    excluded: [
      "Missions terminées (statut ENDED)",
      "Commissions intermédiaires (déduites avant le calcul)",
    ],
    period: "annual",
    source: "finance.ca_annual_net",
    domain: "finance",
    unit: "currency",
    isEstimate: true,
    assumptions: [
      "Les mois sans jours saisis dans les timesheets sont estimés à 18 jours ouvrés par défaut",
      "La valeur peut refléter l'objectif annuel saisi si aucune donnée mission réelle n'est disponible",
    ],
  },

  charges_fixes_annuelles: {
    key: "charges_fixes_annuelles",
    label: "Charges fixes (année)",
    description:
      "Total des charges de structure projetées sur l'ensemble de l'année : loyers, salaires hors consultants, outils, abonnements, etc.",
    formulaText:
      "Σ (montant mensuel de chaque charge active) × nombre de mois d'applicabilité dans l'année",
    included: [
      "Toutes les charges avec is_active = vrai",
      "Charges dont la plage dates couvre tout ou partie de l'année",
    ],
    excluded: [
      "Charges inactives (is_active = faux)",
      "Coûts des consultants (comptés séparément)",
    ],
    period: "annual",
    source: "finance.fixed_costs_annual_total",
    domain: "finance",
    unit: "currency",
  },

  frais_annuels: {
    key: "frais_annuels",
    label: "Frais annuels",
    description:
      "Frais approuvés et en attente déduits de la marge et du résultat net. Ils ne diminuent pas le CA.",
    formulaText:
      "Σ des frais mission et collaborateur pris en compte sur l'année",
    included: [
      "Frais mission approuvés et en attente",
      "Frais collaborateur approuvés et en attente",
      "Tous les mois de l'année en cours",
    ],
    excluded: [
      "Charges fixes de structure",
      "Frais rejetés ou annulés",
    ],
    period: "annual",
    source: "finance.expenses_annual_total",
    domain: "finance",
    unit: "currency",
  },

  benefice_brut_missions_annuel: {
    key: "benefice_brut_missions_annuel",
    label: "Bénéfice brut missions",
    description:
      "Différence entre le CA net et le coût des consultants, après prise en compte des frais mission directs sur l'année. Représente ce que les missions rapportent avant charges fixes de structure.",
    formulaText: "CA annuel net − coûts consultants annuels − frais mission annuels",
    included: [
      "CA net de toutes les missions actives",
      "TJM reversé aux freelances (coût externe)",
      "Coût d'emploi estimé des CDI (salaire × 1,5 / 210 jours)",
      "Frais mission applicables (ponctuels, plage, permanents)",
    ],
    excluded: [
      "Charges fixes de structure",
      "Commissions intermédiaires (déjà déduites du CA net)",
      "Taxes et impôts",
    ],
    period: "annual",
    source: "finance.mission_profit_annual",
    domain: "finance",
    unit: "currency",
    isEstimate: true,
    assumptions: [
      "Attention : ce n'est pas un résultat net — les charges fixes ne sont pas déduites ici",
    ],
  },

  resultat_net_annuel: {
    key: "resultat_net_annuel",
    label: "Résultat net (année)",
    description:
      "Résultat net annuel estimé : ce que la structure dégagerait après déduction des coûts consultants, des frais approuvés et en attente, et des charges fixes structure.",
    formulaText:
      "CA annuel net − coûts consultants − frais annuels − charges fixes annuelles",
    included: [
      "CA net de toutes les missions",
      "Coûts consultants internes et externes",
      "Frais approuvés et en attente mission / collaborateur",
      "Charges fixes structure",
    ],
    excluded: [
      "Taxes et impôts sur les bénéfices",
      "Amortissements",
      "Charges exceptionnelles",
    ],
    period: "annual",
    source: "finance.net_profit_annual",
    domain: "finance",
    unit: "currency",
    isEstimate: true,
    assumptions: [
      "Estimation basée sur les missions actives et les charges saisies",
      "Ne constitue pas un résultat comptable certifié",
    ],
  },

  marge_brute_mission: {
    key: "marge_brute_mission",
    label: "Marge brute mission (%)",
    description:
      "Part du CA conservée après paiement des consultants et des frais mission, avant déduction des charges fixes. Mesure la rentabilité intrinsèque des missions, pas le résultat final de la structure.",
    formulaText:
      "(CA annuel net − coûts consultants − frais mission) / CA annuel net × 100",
    included: [
      "CA net de toutes les missions actives",
      "Coûts consultants (freelances + CDI)",
      "Frais mission applicables",
    ],
    excluded: [
      "Charges fixes de structure (loyers, outils, etc.)",
      "Commissions intermédiaires (déjà déduites du CA net)",
    ],
    period: "annual",
    source: "finance.average_margin_pct",
    domain: "finance",
    unit: "percent",
    assumptions: [
      "⚠ Ce n'est pas une marge nette — les charges fixes ne sont pas déduites ici",
      "La marge nette réelle (après charges fixes) est visible dans le tableau P&L mensuel",
    ],
  },

  // ─── VUE MENSUELLE ───────────────────────────────────────────────────────────

  ca_mensuel_net: {
    key: "ca_mensuel_net",
    label: "CA net estimé (mois courant)",
    description:
      "Chiffre d'affaires net estimé pour le mois en cours, basé sur les jours déclarés ou prévisionnels des timesheets actifs.",
    formulaText:
      "Σ (TJM client − commission intermédiaire) × jours du mois, pour chaque mission active ce mois",
    included: [
      "Missions actives chevauchant le mois courant",
      "Jours déclarés en priorité",
      "Jours prévisionnels si aucune déclaration",
    ],
    excluded: [
      "Missions terminées",
      "Commissions intermédiaires (déduites avant calcul)",
    ],
    period: "monthly",
    source: "finance.ca_monthly_net",
    domain: "finance",
    unit: "currency",
    isEstimate: true,
    assumptions: [
      "Si aucun timesheet n'est saisi pour ce mois, 18 jours ouvrés sont utilisés par défaut",
    ],
  },

  charges_fixes_mensuelles: {
    key: "charges_fixes_mensuelles",
    label: "Charges fixes (mois)",
    description:
      "Total des charges de structure actives et applicables pour le mois courant.",
    formulaText:
      "Σ des charges fixes dont is_active = vrai et dont la plage dates couvre le mois courant",
    included: [
      "Charges avec is_active = vrai",
      "Charges dont la plage dates couvre le mois courant",
    ],
    excluded: [
      "Charges inactives",
      "Charges dont la plage dates ne couvre pas ce mois",
      "Coûts des consultants",
    ],
    period: "monthly",
    source: "finance.fixed_costs_monthly_total",
    domain: "finance",
    unit: "currency",
  },

  resultat_net_mensuel_lisse: {
    key: "resultat_net_mensuel_lisse",
    label: "Résultat net (mois)",
    description:
      "Résultat net du mois courant, calculé depuis la ligne P&L mensuelle après coûts consultants, frais mission et charges fixes applicables.",
    formulaText:
      "Marge brute du mois − charges fixes du mois courant",
    included: [
      "CA net du mois courant",
      "Coûts consultants du mois courant",
      "Frais mission du mois courant",
      "Charges fixes du mois courant",
    ],
    excluded: [
      "Lissage annuel",
      "Mois hors période courante",
    ],
    period: "monthly",
    source: "finance.net_profit_monthly",
    domain: "finance",
    unit: "currency",
    isEstimate: false,
    assumptions: [
      "Pour le détail mois par mois, consulter le tableau P&L détaillé.",
    ],
  },

  // ─── STAFFING & RESSOURCES ───────────────────────────────────────────────────

  consultants_actifs: {
    key: "consultants_actifs",
    label: "Consultants actifs",
    description:
      "Nombre de consultants actuellement positionnés sur au moins une mission active.",
    formulaText:
      "Nombre de collaborateurs distincts liés à au moins une mission avec statut ACTIVE",
    included: [
      "Freelances en mission",
      "CDI en mission",
    ],
    excluded: [
      "Collaborateurs en intercontrat",
      "Collaborateurs inactifs (statut INACTIVE)",
    ],
    period: "instant",
    source: "staffing.active_total",
    domain: "staffing",
    unit: "number",
  },

  consultants_intercontrat: {
    key: "consultants_intercontrat",
    label: "En intercontrat",
    description:
      "Nombre de collaborateurs actifs qui ne sont positionnés sur aucune mission en cours. Indique le nombre de profils à repositionner en priorité.",
    formulaText:
      "Total collaborateurs actifs − consultants en mission",
    included: [
      "Tous les collaborateurs avec statut ≠ INACTIVE",
    ],
    excluded: [
      "Collaborateurs inactifs (statut INACTIVE)",
    ],
    period: "instant",
    source: "intercontrat.count",
    domain: "staffing",
    unit: "number",
  },

  taux_occupation: {
    key: "taux_occupation",
    label: "Taux d'occupation",
    description:
      "Proportion de consultants actuellement en mission active par rapport au total des collaborateurs actifs de la structure.",
    formulaText:
      "Consultants en mission / Total collaborateurs actifs × 100",
    included: [
      "Consultants sur mission active",
      "Tous les collaborateurs non inactifs",
    ],
    excluded: [
      "Collaborateurs inactifs (statut INACTIVE)",
    ],
    period: "instant",
    source: "staffing.active_total + intercontrat.count",
    domain: "staffing",
    unit: "percent",
    assumptions: [
      "Ratio structurel : ne tient pas compte du TACE individuel ni des congés",
    ],
  },

  etp_factures_annuels: {
    key: "etp_factures_annuels",
    label: "ETP facturés (année)",
    description:
      "Équivalents Temps Plein facturés aux clients sur l'année. 1 ETP correspond à 210 jours ouvrés facturés.",
    formulaText:
      "Total jours facturés sur l'année / 210 jours ouvrés de référence",
    included: [
      "Jours déclarés sur les timesheets de l'année",
      "Jours prévisionnels si pas de déclaration",
    ],
    excluded: [
      "Jours en intercontrat (non facturés)",
      "Congés et absences",
    ],
    period: "annual",
    source: "comparisons.annual_billed_etp.actual",
    domain: "staffing",
    unit: "fte",
    assumptions: [
      "Référence : 1 ETP = 210 jours ouvrés / an",
      "Les mois sans timesheet sont estimés à 18 jours ouvrés par défaut",
    ],
  },

  ca_par_consultant: {
    key: "ca_par_consultant",
    label: "CA / consultant actif",
    description:
      "Chiffre d'affaires net annuel ramené par consultant en mission. Mesure la contribution financière unitaire moyenne.",
    formulaText:
      "CA annuel net / nombre de consultants actifs",
    included: [
      "CA annuel net total",
      "Consultants ayant au moins une mission active",
    ],
    excluded: [
      "Consultants en intercontrat (non inclus dans le diviseur)",
    ],
    period: "annual",
    source: "finance.ca_per_consultant_annual",
    domain: "staffing",
    unit: "currency",
  },

  // ─── FORECAST & CONTEXTE ─────────────────────────────────────────────────────

  forecast_annuel: {
    key: "forecast_annuel",
    label: "CA projeté (année)",
    description:
      "Projection du CA annuel net basée sur les missions actives et les timesheets saisis ou prévisionnels pour les mois restants.",
    formulaText:
      "Σ du CA net mensuel projeté pour les 12 mois de l'année",
    included: [
      "Missions actives sur chaque mois",
      "Jours déclarés (priorité) ou prévisionnels",
    ],
    excluded: [
      "Nouvelles missions non encore créées",
      "Missions avec statut ENDED",
    ],
    period: "annual",
    source: "forecast.projected_ca_annual_net",
    domain: "finance",
    unit: "currency",
    isEstimate: true,
    assumptions: [
      "Mois sans timesheet : 18 jours ouvrés par défaut",
      "La fiabilité dépend du pourcentage de mois couverts par des jours déclarés",
    ],
  },

  objectif_proratise: {
    key: "objectif_proratise",
    label: "Objectif proratisé (YTD)",
    description:
      "Part de l'objectif annuel de CA attendue au prorata du nombre de mois écoulés depuis janvier.",
    formulaText:
      "Objectif CA annuel × (mois écoulés depuis janvier / 12)",
    included: [
      "Objectif CA annuel saisi dans les objectifs",
      "Nombre de mois depuis janvier de l'année en cours",
    ],
    excluded: [
      "Saisonnalité ou variations mensuelles planifiées",
    ],
    period: "annual",
    source: "temporal.prorated_target",
    domain: "finance",
    unit: "currency",
  },
};

/**
 * Récupère la définition complète d'un KPI par sa clé.
 * Retourne null si la clé n'existe pas.
 */
export function getDashboardKpi(key: string): DashboardKpiDefinition | null {
  return DASHBOARD_KPI_REGISTRY[key] ?? null;
}

/**
 * Récupère le label court d'un KPI.
 * Retourne la clé brute si le KPI n'existe pas.
 */
export function getDashboardKpiLabel(key: string): string {
  return DASHBOARD_KPI_REGISTRY[key]?.label ?? key;
}

/**
 * Récupère la description d'un KPI.
 * Retourne une chaîne vide si le KPI n'existe pas.
 */
export function getDashboardKpiDescription(key: string): string {
  return DASHBOARD_KPI_REGISTRY[key]?.description ?? "";
}

/**
 * Retourne toutes les clés du registre dans l'ordre de déclaration.
 */
export function getAllDashboardKpiKeys(): string[] {
  return Object.keys(DASHBOARD_KPI_REGISTRY);
}
